import { getFullContentDimensions } from './exportHelpers';

/**
 * html2canvas corta a última linha quando o texto está com line-clamp ou overflow hidden.
 * Solta só o recorte do texto. Não mexe em altura, largura, transform nem no losango.
 */
function unclipCardText(clonedDoc: Document, root: HTMLElement) {
  const style = clonedDoc.createElement('style');
  style.textContent = `
    #canvas-content [data-card-id]:not(.rounded-full) .overflow-hidden,
    #canvas-content [data-card-id]:not(.rounded-full) .truncate,
    #canvas-content [data-card-id]:not(.rounded-full) [class*="line-clamp"] {
      overflow: visible !important;
      text-overflow: clip !important;
      -webkit-line-clamp: unset !important;
      line-clamp: unset !important;
    }
  `;
  clonedDoc.head.appendChild(style);

  root.querySelectorAll<HTMLElement>('[data-card-id]:not(.rounded-full) *').forEach((element) => {
    if (element.closest('[data-card-type="decisao"], [data-decision-content]')) return;
    if (element.style.webkitLineClamp) element.style.webkitLineClamp = 'unset';
    if (element.style.overflow === 'hidden') element.style.overflow = 'visible';
    if (element.style.textOverflow === 'ellipsis') element.style.textOverflow = 'clip';
  });
}

/** html2canvas erra translate em porcentagem. Converte para pixels antes de desenhar. */
function freezePercentTransforms(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('*').forEach((element) => {
    const inline = element.style.transform;
    if (!inline || !inline.includes('%')) return;
    const width = element.offsetWidth || element.getBoundingClientRect().width;
    const height = element.offsetHeight || element.getBoundingClientRect().height;
    element.style.transform = inline
      .replace(/translate\(\s*(-?\d+(?:\.\d+)?)%\s*,\s*(-?\d+(?:\.\d+)?)%\s*\)/g, (_, x: string, y: string) =>
        `translate(${(parseFloat(x) / 100) * width}px, ${(parseFloat(y) / 100) * height}px)`)
      .replace(/translateX\(\s*(-?\d+(?:\.\d+)?)%\s*\)/g, (_, x: string) =>
        `translateX(${(parseFloat(x) / 100) * width}px)`)
      .replace(/translateY\(\s*(-?\d+(?:\.\d+)?)%\s*\)/g, (_, y: string) =>
        `translateY(${(parseFloat(y) / 100) * height}px)`);
  });
}

/**
 * Exporta o canvas como imagem PNG
 */
export const exportCanvasToImage = async (
  canvasElement: HTMLElement,
  projectName: string = 'Workflow',
  format: 'png' | 'jpg' = 'png'
): Promise<void> => {
  try {
    console.log('[exportImage] Iniciando exportação de imagem...');
    console.log('[exportImage] Elemento recebido:', canvasElement);
    console.log('[exportImage] Elemento id:', canvasElement.id);
    console.log('[exportImage] Elemento dimensões originais:', { 
      offsetWidth: canvasElement.offsetWidth, 
      offsetHeight: canvasElement.offsetHeight,
      scrollWidth: canvasElement.scrollWidth,
      scrollHeight: canvasElement.scrollHeight
    });
    
    // Importar html2canvas dinamicamente
    const html2canvas = (await import('html2canvas')).default;
    
    console.log('[exportImage] html2canvas importado com sucesso');
    
    // Obter dimensões completas do conteúdo (sem modificar elementos visíveis)
    const { width, height } = getFullContentDimensions(canvasElement);
    
    console.log('[exportImage] Dimensões calculadas:', { width, height });
    
    if (width <= 0 || height <= 0) {
      throw new Error(`Dimensões inválidas: width=${width}, height=${height}`);
    }
    
    // Criar canvas da imagem com dimensões completas
    const canvas = await html2canvas(canvasElement, {
      backgroundColor: '#ffffff',
      scale: 2, // Qualidade balanceada para imagem
      useCORS: true,
      logging: false,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      width: width,
      height: height,
      windowWidth: width,
      windowHeight: height,
      onclone: (clonedDoc) => {
        clonedDoc.body.style.background = '#ffffff';
        const clonedElement = clonedDoc.querySelector('#canvas-content') as HTMLElement | null;
        if (!clonedElement) return;
        clonedElement.style.overflow = 'visible';
        clonedElement.style.position = 'relative';
        clonedElement.style.width = `${width}px`;
        clonedElement.style.height = `${height}px`;
        clonedElement.style.transform = 'none';
        clonedElement.style.left = '0px';
        clonedElement.style.top = '0px';
        clonedElement.style.backgroundColor = '#ffffff';

        // O SVG das setas fica. Os SVGs dos losangos e dos cards mantêm o tamanho original.
        clonedElement.querySelectorAll('svg').forEach((svg) => {
          if (svg.closest('[data-card-id], [data-decision-content]')) return;
          svg.style.visibility = 'visible';
          svg.style.overflow = 'visible';
        });

        unclipCardText(clonedDoc, clonedElement);
        freezePercentTransforms(clonedElement);
      },
    });

    // Não precisamos restaurar estilos do elemento original pois não os modificamos

    // Converter para blob e fazer download
    const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = format === 'jpg' ? 0.95 : undefined;
    
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Falha ao criar o arquivo de imagem.');
      }
      
      // Criar URL temporária
      const url = URL.createObjectURL(blob);
      
      // Criar link de download
      const link = document.createElement('a');
      link.href = url;
      
      // Sanitizar nome do arquivo
      const sanitizedName = projectName.replace(/[^a-z0-9]/gi, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const extension = format === 'jpg' ? 'jpg' : 'png';
      link.download = `${sanitizedName}_${dateStr}.${extension}`;
      
      // Disparar download
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, mimeType, quality);
  } catch (error) {
    console.error('Erro ao exportar imagem:', error);
    throw new Error('Falha ao exportar imagem. Tente novamente.');
  }
};

